import type { ScoOption } from './sco-navigation'
import { useQuery } from '@apollo/client/react'
import { useCallback, useState } from 'react'
import { GQL_MODULE_SCOS } from '~/gql/scorm/scorm'
import { activeSco } from './sco-navigation'

type Resume = {
  currentScoIdentifier?: string | null
  currentAttempt?: { scoStates?: { scoIdentifier: string, completionStatus: string }[] } | null
} | null | undefined

/**
 * Les chapitres d'un module à plusieurs SCO (#160) : lequel jouer, lesquels
 * sont faits. Un module à un seul SCO n'en a qu'un — `multi` faux — et se joue
 * comme avant.
 */
export function useScoNavigation(moduleId: string | undefined, resume: Resume) {
  const { data, loading } = useQuery(GQL_MODULE_SCOS, { variables: { moduleId: moduleId ?? '' }, skip: !moduleId })
  const scos: ScoOption[] = data?.moduleScos ?? []
  const [chosen, setChosen] = useState<string | null>(null)
  // Faits pendant cette séance, en plus de ceux que la tentative avait déjà.
  const [doneNow, setDoneNow] = useState<ReadonlySet<string>>(() => new Set())
  const doneBefore = (resume?.currentAttempt?.scoStates ?? [])
    .filter(state => state.completionStatus === 'COMPLETED')
    .map(state => state.scoIdentifier)

  return {
    loading,
    scos,
    multi: scos.length > 1,
    active: activeSco(scos, chosen, resume?.currentScoIdentifier),
    completed: new Set([...doneBefore, ...doneNow]) as ReadonlySet<string>,
    select: setChosen,
    // Stable : le lecteur la passe à `onCommit`, dont le changement remonterait le bridge.
    markCompleted: useCallback((identifier: string) => setDoneNow(current => new Set([...current, identifier])), []),
  }
}
